/**
 * API Service
 * Centralized API client for backend communication
 */
import axios from 'axios';
import { getPublicApiOrigin } from '../config/env';

// Axios joins baseURL + url by stripping a leading "/" from url, so baseURL "/api" with
// path "/api/v1/..." becomes "/api/api/v1/..." (404 behind nginx). Use "" when paths already include /api/.
const rawBase = getPublicApiOrigin();
const API_URL =
  rawBase === '/api' || rawBase === '/api/' ? '' : rawBase.replace(/\/$/, '');

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for adding auth token
api.interceptors.request.use(
  (config) => {
    const token =
      localStorage.getItem('token') ||
      localStorage.getItem('access_token') ||
      localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors
let _redirectingToLogin = false;
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const url = String(error.config?.url ?? '');
    const isAuthRoute =
      url.includes('/auth/login') ||
      url.includes('/auth/password-login') ||
      url.includes('/auth/otp/') ||
      url.includes('/auth/verify-otp') ||
      url.includes('/auth/register') ||
      url.includes('/auth/cross-app') ||
      url.includes('/auth/me');  // /auth/me 401 is handled by AdminLogin; don't redirect
    const onLoginPage = window.location.pathname.startsWith('/admin/login');
    if (status === 401 && !isAuthRoute && !onLoginPage && !_redirectingToLogin) {
      _redirectingToLogin = true;
      localStorage.removeItem('token');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

// Auth API (Legacy - OTP-based for candidates)
export const authAPI = {
  login: (email) => api.post('/api/auth/login', { email }),
  verifyOTP: (email, code) => api.post('/api/auth/verify-otp', { email, code }),
  register: (data) => api.post('/api/auth/register', data),
};

// Admin Auth API
// Microsoft SSO is handled by MSAL in AdminLogin.jsx (Azure ID token in localStorage).
// Password and OTP issue a backend admin session JWT, also stored as localStorage.token.
export const adminAuthAPI = {
  getCurrentAdmin: () => api.get('/api/v1/auth/me'),
  logout: () => api.post('/api/v1/auth/logout'),
  passwordLogin: (email, password) =>
    api.post('/api/v1/auth/password-login', { email, password }),
  requestOtp: (email) => api.post('/api/v1/auth/otp/request', { email }),
  verifyOtp: (email, code) => api.post('/api/v1/auth/otp/verify', { email, code }),
  consumeHandoff: (code, source = 'reachout') =>
    api.post('/api/v1/auth/cross-app/consume', { code, source }),
  createHandoff: (target = 'reachout') =>
    api.post('/api/v1/auth/cross-app-handoff', { target }),
};

// Wizetok Administration — tenant users and their Wizetok roles (identity-backed).
export const adminUsersAPI = {
  list: () => api.get('/api/v1/admin/users'),
  add: (email, roles, name) => api.post('/api/v1/admin/users', { email, roles, name }),
  updateRoles: (email, roles) => api.put(`/api/v1/admin/users/${encodeURIComponent(email)}`, { roles }),
  remove: (email) => api.delete(`/api/v1/admin/users/${encodeURIComponent(email)}`),
};

// Rules & Permissions (same shape as ReachOut's /rules)
export const rulesAPI = {
  me: () => api.get('/api/v1/admin/rules/me', { params: { _: Date.now() } }),
  catalog: () => api.get('/api/v1/admin/rules/catalog'),
  company: () => api.get('/api/v1/admin/rules/company'),
  saveCompany: (body) => api.put('/api/v1/admin/rules/company', body),
  resetCompany: () => api.post('/api/v1/admin/rules/company/reset'),
  team: (manager) => api.get('/api/v1/admin/rules/team', { params: { manager } }),
  saveTeam: (manager, body) => api.put('/api/v1/admin/rules/team', body, { params: { manager } }),
  resetTeam: (manager) => api.post('/api/v1/admin/rules/team/reset', null, { params: { manager } }),
  managers: () => api.get('/api/v1/admin/rules/managers'),
  audit: (limit = 200) => api.get('/api/v1/admin/rules/audit', { params: { limit } }),
  link: () => api.get('/api/v1/admin/rules/link'),
  setLink: (enabled) => api.put('/api/v1/admin/rules/link', { enabled }),
};

// Campaign access: assign people, lock, transfer, pick up
export const campaignAccessAPI = {
  assignableUsers: () => api.get('/api/v1/campaigns/assignable-users'),
  myAccess: (id) => api.get(`/api/v1/campaigns/${id}/my-access`, { params: { _: Date.now() } }),
  members: (id) => api.get(`/api/v1/campaigns/${id}/members`),
  setMembers: (id, members) => api.put(`/api/v1/campaigns/${id}/members`, members),
  pickUp: (id) => api.post(`/api/v1/campaigns/${id}/pick-up`),
  transfer: (id, email) => api.post(`/api/v1/campaigns/${id}/transfer`, { email }),
  setLocked: (id, locked) => api.post(`/api/v1/campaigns/${id}/lock`, { locked }),
};

// Candidate API
export const candidateAPI = {
  getProfile: () => api.get('/api/candidate/profile'),
  uploadResume: (formData) => api.post('/api/candidate/upload-resume', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getResume: () => api.get('/api/candidate/resume'),
  systemCheck: () => api.get('/api/candidate/system-check'),
  getInterviews: () => api.get('/api/candidate/interviews'),
  getInterview: (id) => api.get(`/api/candidate/interviews/${id}`),
};

// Interview API
export const interviewAPI = {
  create: (data) => api.post('/api/interview/create', data),
  getInterview: (id) => api.get(`/api/interview/${id}`),
  startInterview: (id) => api.post(`/api/interview/${id}/start`),
  endInterview: (id) => api.post(`/api/interview/${id}/end`),
  getStatus: (id) => api.get(`/api/interview/${id}/status`),
};

// Evaluation API
export const evaluationAPI = {
  getEvaluation: (interviewId) => api.get(`/api/evaluation/${interviewId}`),
};

// Campaign API
export const campaignAPI = {
  list: (isActive) => api.get('/api/v1/campaigns', { params: { is_active: isActive } }),
  // Dashboard / Analytics: company-wide numbers for people allowed to view company analytics.
  listForStats: (isActive) => api.get('/api/v1/campaigns', { params: { is_active: isActive, scope: 'stats' } }),
  get: (campaignId) => api.get(`/api/v1/campaigns/${campaignId}`),
  create: (data) => api.post('/api/v1/campaigns', data),
  update: (campaignId, data) => api.put(`/api/v1/campaigns/${campaignId}`, data),
  patch: (campaignId, data) => api.patch(`/api/v1/campaigns/${campaignId}`, data),
  delete: (campaignId) => api.delete(`/api/v1/campaigns/${campaignId}`),
  getStatistics: (campaignId) => api.get(`/api/v1/campaigns/${campaignId}/statistics`),
  getBulkStatistics: () => api.get('/api/v1/campaigns/statistics/bulk'),
  suggestSkills: (data) => api.post('/api/v1/campaigns/suggest-skills', data),
  extractJd: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/v1/campaigns/extract-jd', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  previewQuestions: (data, config) => api.post('/api/v1/campaigns/preview-questions', data, config),
  regenerateQuestion: (data, config) => api.post('/api/v1/campaigns/regenerate-question', data, config),
  previewEnglishQuestions: (data, config) => api.post('/api/v1/campaigns/preview-english-questions', data, config),
  previewCodingQuestions: (data, config) => api.post('/api/v1/campaigns/preview-coding-questions', data, config),
  regenerateEnglishQuestion: (data, config) => api.post('/api/v1/campaigns/regenerate-english-question', data, config),
  getQuestions: (campaignId) =>
    api.get(`/api/v1/campaigns/${campaignId}/questions`),
  updateQuestions: (campaignId, questions) =>
    api.put(`/api/v1/campaigns/${campaignId}/questions`,
      { questions }),
  updateDuration: (campaignId, override) =>
    api.patch(`/api/v1/campaigns/${campaignId}/duration`, { override }),
  groqGenerateQuestions: (data, config) => api.post('/api/campaigns/generate-questions', data, config),
  groqRegenerateQuestion: (data, config) => api.post('/api/campaigns/regenerate-question', data, config),
  listQuestions: (campaignId) => api.get(`/api/v1/campaigns/${campaignId}/questions`),
  saveQuestions: (campaignId, questions) => api.post(`/api/v1/campaigns/${campaignId}/questions`, { questions }),
  // Question Bank — cross-campaign, position-based pool of AI-generated questions
  getQuestionBank: (role) => api.get('/api/v1/campaigns/questions/bank', { params: role ? { role } : {} }),
  getQuestionBankByPosition: () => api.get('/api/v1/campaigns/questions/bank/by-position'),
  deduplicateQuestionBank: (role) => api.post('/api/v1/campaigns/questions/bank/deduplicate', null, { params: role ? { role } : {} }),
  // Resume endpoints
  uploadResume: (campaignId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/v1/campaigns/${campaignId}/resumes`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadBulkResumes: (campaignId, files) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    return api.post(`/api/v1/campaigns/${campaignId}/resumes/bulk`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  listResumes: (campaignId) => api.get(`/api/v1/campaigns/${campaignId}/resumes`),
  deleteResume: (campaignId, resumeId) => api.delete(`/api/v1/campaigns/${campaignId}/resumes/${resumeId}`),
  // Candidate endpoints
  listCandidates: (campaignId) => api.get(`/api/v1/campaigns/${campaignId}/candidates`),
  createCandidate: (campaignId, data) => api.post(`/api/v1/campaigns/${campaignId}/candidates`, data),
  createCandidateWithResume: (campaignId, formData) =>
    api.post(`/api/v1/campaigns/${campaignId}/candidates/with-resume`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  updateCandidate: (campaignId, candidateId, data) => api.put(`/api/v1/campaigns/${campaignId}/candidates/${candidateId}`, data),
  setShortlisted: (campaignId, candidateId, isShortlisted) =>
    api.patch(`/api/v1/campaigns/${campaignId}/candidates/${candidateId}/shortlist`, { is_shortlisted: isShortlisted }),
  deleteCandidate: (campaignId, candidateId) => api.delete(`/api/v1/campaigns/${campaignId}/candidates/${candidateId}`),
  uploadResumesForCandidates: (campaignId, files, names = [], emails = [], examSetId = null) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    if (names.length) formData.append('names', JSON.stringify(names));
    if (emails.length) formData.append('emails', JSON.stringify(emails));
    if (examSetId) formData.append('exam_set_id', examSetId);
    return api.post(`/api/v1/campaigns/${campaignId}/candidates/upload-resumes`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  generateInviteLinks: (campaignId, candidateIds = null, all = false) => {
    const params = all ? { all: true } : { candidate_ids: candidateIds };
    return api.post(`/api/v1/campaigns/${campaignId}/candidates/generate-links`, null, { params });
  },
  resendInvite: (campaignId, candidateId) => api.post(`/api/v1/campaigns/${campaignId}/candidates/${candidateId}/resend-invite`),
};

// Public API (for candidates via link)
export const publicAPI = {
  getCampaignByLink: (link) => api.get(`/api/v1/public/campaign/${link}`),
  registerCandidate: (formData) => api.post('/api/v1/public/register', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getSessionInfo: (sessionId) => api.get(`/api/v1/public/session/${sessionId}/info`),
  getSessionStatus: (sessionId, accessToken) => api.get(`/api/v1/public/session/${sessionId}/status`, {
    params: { access_token: accessToken }
  }),
};

// Interview Session API (campaign-based)
export const sessionAPI = {
  get: (sessionId) => api.get(`/api/v1/sessions/${sessionId}`),
  start: (sessionId) => api.post(`/api/v1/sessions/${sessionId}/start`),
  getStatus: (sessionId) => api.get(`/api/v1/sessions/${sessionId}/status`),
  // Get session status with token (public endpoint)
  getStatusWithToken: (sessionId, accessToken) => api.get(`/api/v1/public/session/${sessionId}/status`, {
    params: { access_token: accessToken }
  }),
  // Admin endpoints
  getAllSessions: (params) => api.get(`/api/v1/sessions/admin/all`, { params }),
  /** Interrupted exam: email the candidate the link to continue (app/services/exam_resume.py). */
  sendResumeEmail: (sessionId) => api.post(`/api/v1/sessions/admin/${sessionId}/resume-email`),
};

// Exam Sets — multiple named tests within a campaign
export const examSetAPI = {
  list: (campaignId) => api.get(`/api/v1/campaigns/${campaignId}/exam-sets`),
  get: (campaignId, examSetId) => api.get(`/api/v1/campaigns/${campaignId}/exam-sets/${examSetId}`),
  create: (campaignId, data) => api.post(`/api/v1/campaigns/${campaignId}/exam-sets`, data),
  update: (campaignId, examSetId, data) => api.patch(`/api/v1/campaigns/${campaignId}/exam-sets/${examSetId}`, data),
  remove: (campaignId, examSetId) => api.delete(`/api/v1/campaigns/${campaignId}/exam-sets/${examSetId}`),
  assignCandidates: (campaignId, examSetId, candidateIds) =>
    api.post(`/api/v1/campaigns/${campaignId}/exam-sets/${examSetId}/candidates`, { candidate_ids: candidateIds }),
  unassignCandidate: (campaignId, examSetId, candidateId) =>
    api.delete(`/api/v1/campaigns/${campaignId}/exam-sets/${examSetId}/candidates/${candidateId}`),
  importSyncedCandidates: (campaignId, examSetId, syncedCandidateIds) =>
    api.post(`/api/v1/campaigns/${campaignId}/exam-sets/${examSetId}/import-synced-candidates`, { synced_candidate_ids: syncedCandidateIds }),
};

// Batches of an interview (exam set): backend app/api/routes/batches.py.
// A batch is { id, exam_set_id, campaign_id, name, opens_at, closes_at,
//   status: 'scheduled' | 'open' | 'closed', created_at, stats }.
export const batchAPI = {
  listForCampaign: (campaignId) => api.get(`/api/v1/campaigns/${campaignId}/batches`),
  listForExamSet: (campaignId, examSetId) => api.get(`/api/v1/campaigns/${campaignId}/exam-sets/${examSetId}/batches`),
  update: (campaignId, examSetId, batchId, data) =>
    api.patch(`/api/v1/campaigns/${campaignId}/exam-sets/${examSetId}/batches/${batchId}`, data),
  create: (campaignId, examSetId, data) =>
    api.post(`/api/v1/campaigns/${campaignId}/exam-sets/${examSetId}/batches`, data),
  assignCandidates: (campaignId, examSetId, candidateIds, batchId) =>
    api.post(`/api/v1/campaigns/${campaignId}/exam-sets/${examSetId}/candidates`, { candidate_ids: candidateIds, batch_id: batchId }),
  importSyncedCandidates: (campaignId, examSetId, syncedCandidateIds, batchId) =>
    api.post(`/api/v1/campaigns/${campaignId}/exam-sets/${examSetId}/import-synced-candidates`, { synced_candidate_ids: syncedCandidateIds, batch_id: batchId }),
  // Bulk invite chosen candidates of one batch -> { sent_count, sent, skipped, failed }.
  invite: (campaignId, examSetId, batchId, candidateIds) =>
    api.post(`/api/v1/campaigns/${campaignId}/exam-sets/${examSetId}/batches/${batchId}/invite`, { candidate_ids: candidateIds }),
};

// Coding-question bank — campaign-scoped, used by exam_type="coding" tests
export const codingQuestionAPI = {
  list: (campaignId) => api.get(`/api/v1/campaigns/${campaignId}/coding-questions`),
  get: (campaignId, questionId) =>
    api.get(`/api/v1/campaigns/${campaignId}/coding-questions/${questionId}`),
  create: (campaignId, data) => api.post(`/api/v1/campaigns/${campaignId}/coding-questions`, data),
  generate: (campaignId, data = {}) =>
    api.post(`/api/v1/campaigns/${campaignId}/coding-questions/generate`, data),
  generateTestCases: (campaignId, data) =>
    api.post(`/api/v1/campaigns/${campaignId}/coding-questions/generate-testcases`, data),
  remove: (campaignId, questionId) => api.delete(`/api/v1/campaigns/${campaignId}/coding-questions/${questionId}`),
};

// Interview Report PDFs (backend app/services/reports). PDFs come back as blobs
// so the admin token from the interceptor is sent; `refresh` regenerates the AI
// text and re-renders instead of using the cached PDF.
export const reportAPI = {
  sessionPdf: (sessionId, refresh = false) =>
    api.get(`/api/reports/sessions/${sessionId}/pdf`, { responseType: 'blob', params: refresh ? { refresh: true } : {} }),
  sessionJson: (sessionId) => api.get(`/api/reports/sessions/${sessionId}`),
  /** { parts: [{ part, url, started_at, duration_seconds }], events: [{ part, offset, label, severity, … }] } */
  sessionRecording: (sessionId) => api.get(`/api/reports/sessions/${sessionId}/recording`),
  campaignCandidates: (campaignId) => api.get(`/api/reports/campaigns/${campaignId}/candidates`),
  campaignPdf: (campaignId, email, refresh = false) =>
    api.get(`/api/reports/campaigns/${campaignId}/report/pdf`, {
      responseType: 'blob', params: refresh ? { email, refresh: true } : { email },
    }),
  startBatch: (campaignId) => api.post(`/api/reports/campaigns/${campaignId}/batch`),
  batchJob: (jobId) => api.get(`/api/reports/jobs/${jobId}`),
};

/** Filename from a Content-Disposition header, or `fallback`. */
export function filenameFromResponse(response, fallback) {
  const header = response?.headers?.['content-disposition'] || '';
  const match = /filename="?([^";]+)"?/i.exec(header);
  return match ? match[1] : fallback;
}

/** Error text from a blob (responseType: 'blob') or JSON error response. */
export async function reportErrorMessage(error) {
  const data = error?.response?.data;
  try {
    const parsed = data instanceof Blob ? JSON.parse(await data.text()) : data;
    if (parsed?.detail) return typeof parsed.detail === 'string' ? parsed.detail : JSON.stringify(parsed.detail);
  } catch (_) { /* not JSON */ }
  return error?.message || 'The report could not be loaded.';
}

export default api;

